import React, { useState } from 'react';
import {
  Paper, Typography, Button, Stack, Chip, Alert, AlertTitle, CircularProgress, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Box, ToggleButton, ToggleButtonGroup, Link, Tooltip,
} from '@mui/material';
import { Refresh, PhoneAndroid, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAppVersionAdoption, AppVersionAdoption } from '../../services/cityOperationService';
import { opsErrorMessage } from './opsErrors';

/**
 * APP-VERSION ADOPTION — the panel that answers "can I raise LOCATION_PING_MIN_APP_VERSION yet?".
 *
 * Two deliberate choices about what it leads with:
 *
 *  1. `stallingPct`, not `eligiblePct`. The obvious headline number is diluted by dormant accounts
 *     that will never update. "Of the users whose push token was re-registered this week (a proxy for
 *     app opens, not a count of them), how many are still on an old build" is the number that moves.
 *  2. Every cohort is a LINK into the filtered driver list. A count you cannot act on is trivia; the
 *     point of "667 on an old build" is to go see who they are.
 */
interface Props {
  /** The city selected on the City Operations page; empty means fleet-wide. */
  city?: string;
  /** REENGAGE_SMS_ENABLED as saved; undefined = not known (config not loaded). */
  smsRailOn?: boolean;
}

/** The SMS-rail sentence for the no-token warning, from the saved switch rather than a hard-coded "off". */
export const smsRailSentence = (on?: boolean): string =>
  on === undefined
    ? 'The SMS re-engagement rail exists for this cohort — check REENGAGE_SMS_ENABLED in Dispatch Tuning.'
    : on
      ? 'The SMS re-engagement rail exists for this cohort and is switched ON (REENGAGE_SMS_ENABLED = 1); it delivers only once the DLT template is approved.'
      : 'The SMS re-engagement rail exists for this cohort; it is switched OFF (REENGAGE_SMS_ENABLED = 0).';

const AppAdoptionPanel: React.FC<Props> = ({ city, smsRailOn }) => {
  const navigate = useNavigate();
  const [role, setRole] = useState<'driver' | 'rider' | 'all'>('driver');
  const [state, setState] = useState<{ data: AppVersionAdoption | null; busy: boolean; error: string | null }>({
    data: null, busy: false, error: null,
  });

  const load = async (nextRole = role, fresh = false) => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const res = await getAppVersionAdoption(city || undefined, nextRole, fresh);
      setState({ data: res.data, busy: false, error: null });
    } catch (e: any) {
      setState({
        data: null, busy: false,
        error: opsErrorMessage(e, 'Could not load adoption.'),
      });
    }
  };

  /**
   * Deep-link into the list, pre-filtered to a cohort. Riders live on /users, drivers on /drivers.
   * The rider link MUST carry role=rider: the user list passes its role filter to the backend, and
   * the backend uses it to pick WHICH version column the appVersion filter targets — without it the
   * landing page would filter driverAppVersion and show a different (usually empty) cohort than the
   * number that was clicked.
   */
  const drillTo = (appVersion: string) =>
    navigate(role === 'rider'
      ? `/users?role=rider&appVersion=${appVersion}`
      : `/drivers?appVersion=${appVersion}`);

  const CountLink: React.FC<{ value: number; to: string; title: string }> = ({ value, to, title }) => (
    <Tooltip title={title}>
      <Link component="button" underline="hover" onClick={() => drillTo(to)}
            sx={{ fontWeight: 500, cursor: 'pointer' }}>
        {value.toLocaleString()}
      </Link>
    </Tooltip>
  );

  const d = state.data;
  const seenRecently = d ? d.unknownActive + d.knownActive : 0;
  const totalInstalls = d ? d.totalReachable + d.tokenless : 0;
  const tokenlessPct = d && totalInstalls ? Math.round((d.tokenless / totalInstalls) * 1000) / 10 : 0;

  /** The single next action, derived rather than left for the reader to infer. */
  const verdict = (): { severity: 'success' | 'warning' | 'info'; title: string; body: string } | null => {
    if (!d) return null;
    if (!d.gateActive && d.gateAppliesToRole) {
      return { severity: 'warning', title: 'The gate is off',
        body: 'LOCATION_PING_MIN_APP_VERSION is 0. If the probe/sweep rails are on, every driver is pinged including old builds, which render it as a junk notification.' };
    }
    if (seenRecently < 20) {
      return { severity: 'info', title: 'Not enough recent activity to judge',
        body: `Only ${seenRecently} ${d.role}s re-registered their push token in the last ${d.activeDays} days. Give the rollout more time before reading the trend.` };
    }
    if (d.stallingPct > 60) {
      return { severity: 'warning', title: 'Rollout is stalling',
        body: `${d.stallingPct}% of recently re-registered ${d.role}s are still on an old build. If this does not fall over the next week, consider raising recommendedVersionCode in ANDROID_UPDATE_POLICY.` };
    }
    if (d.stallingPct > 20) {
      return { severity: 'info', title: 'Rollout in progress',
        body: `${d.stallingPct}% of recently re-registered ${d.role}s are still behind. Normal mid-rollout — keep watching rather than forcing an update.` };
    }
    return { severity: 'success', title: 'Adoption is healthy',
      body: `Only ${d.stallingPct}% of recently re-registered ${d.role}s are behind. Safe to raise LOCATION_PING_MIN_APP_VERSION to the current build when you ship the next one.` };
  };
  const v = verdict();

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <PhoneAndroid color="primary" />
        <Typography variant="h6">App version adoption</Typography>
      </Stack>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        How much of the fleet can service a silent location ping yet. A version is reported when the app
        <strong> re-registers its push token </strong> (usually on open), not when Play Store installs it — so
        this lags real installs. "Recently" below means the token was re-registered, not a count of app opens.
        Counts are clickable.
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: -1.5, mb: 2 }} data-testid="adoption-counts">
        Counts: accounts in the users table (not driver profiles){city ? ` whose current or home city is ${city}` : ', fleet-wide'}, for
        the role chosen below; suspended accounts left out. The App Type selector is not applied.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={role}
          onChange={(_e, val) => { if (val) { setRole(val); if (state.data) load(val); } }}>
          <ToggleButton value="driver">Drivers</ToggleButton>
          <ToggleButton value="rider">Riders</ToggleButton>
          <ToggleButton value="all">Both</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="outlined" onClick={() => load()} disabled={state.busy}
          endIcon={state.busy ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
          sx={{ borderRadius: 2 }}>
          {city ? `Load for ${city}` : 'Load fleet-wide'}
        </Button>
        {d && (
          <>
            <Chip size="small" variant="outlined"
                  label={d.source === 'snapshot' ? `As of ${d.asOf}` : 'Live (just computed)'} />
            <Button variant="text" size="small" onClick={() => load(role, true)} disabled={state.busy}>
              Recompute now
            </Button>
          </>
        )}
      </Stack>

      {state.error && <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>{state.error}</Alert>}

      {d && (
        <>
          {/* A truncated scan means the totals are UNDERCOUNTS. Without this they would read as a
              genuine collapse in adoption and send someone chasing a phantom. */}
          {d.scan?.truncated && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              <AlertTitle>These numbers are incomplete</AlertTitle>
              The scan stopped at its safety ceiling after {d.scan.rows.toLocaleString()} rows, so every
              count below is an undercount — not a real drop. Check the worker logs for
              <code> [chunkedScan] </code> and raise the chunk size or the ceiling.
            </Alert>
          )}

          {!d.gateAppliesToRole && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              The location-ping gate is driver-only. For {d.role === 'all' ? 'this combined view' : 'riders'} these
              versions are reporting data — nobody here is pinged regardless of build.
            </Alert>
          )}

          {v && (
            <Alert severity={v.severity} variant="outlined" sx={{ mb: 2 }}>
              <AlertTitle>{v.title}</AlertTitle>{v.body}
            </Alert>
          )}

          {/* Push-dark is a bigger reach problem than app version and deserves to be seen next to it,
              not buried in a chip — a driver with no token cannot be reached by ANY push feature. */}
          {tokenlessPct >= 15 && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <AlertTitle>{d.tokenless.toLocaleString()} {d.role}s ({tokenlessPct}%) have no push token at all</AlertTitle>
              These are unreachable by every push feature, not just location pings — no ride offers, no
              nudges, no digests. App version is irrelevant for them. {smsRailSentence(smsRailOn)}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Is the rollout progressing?</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Of {seenRecently.toLocaleString()} whose push token was re-registered in the last {d.activeDays} days,{' '}
            <CountLink value={d.unknownActive} to="unknown" title="Open the list filtered to old-app users" />{' '}
            are still on an old build.
          </Typography>
          <LinearProgress variant="determinate" value={Math.min(100, d.stallingPct)}
            color={d.stallingPct > 60 ? 'warning' : 'success'}
            sx={{ height: 8, borderRadius: 4, mb: 1 }} />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip size="small" variant="outlined" color={d.stallingPct > 60 ? 'warning' : 'success'}
                  label={`${d.stallingPct}% of recently re-registered users still on an old build`} />
            {d.trend && (
              <>
                <Chip size="small" variant="outlined" color={d.trend.stallingPct < 0 ? 'success' : 'default'}
                      label={`${d.trend.stallingPct > 0 ? '+' : ''}${d.trend.stallingPct} pts stalling vs ${d.trend.since}`} />
                <Chip size="small" variant="outlined" color={d.trend.eligiblePct > 0 ? 'success' : 'default'}
                      label={`${d.trend.eligiblePct > 0 ? '+' : ''}${d.trend.eligiblePct} pts eligible`} />
              </>
            )}
          </Stack>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Fleet totals</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <Chip size="small" variant="outlined" color="primary"
                  label={`Gate: ${d.gateActive ? `build ≥ ${d.minAppVersion}` : 'OFF'}`} />
            <Chip size="small" variant="outlined" label={`${d.totalReachable.toLocaleString()} with a push token stored`} />
            <Typography variant="body2" color="textSecondary">
              <CountLink value={d.eligible} to="eligible" title="Open the list filtered to probe-eligible users" />
              {` eligible (${d.eligiblePct}%) · `}
              <CountLink value={d.unknown} to="unknown" title="Open the list filtered to old-app users" />
              {' on an unknown build'}
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Version breakdown</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Build</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right">Share</TableCell>
                  <TableCell>Pingable</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {d.versions.map((row) => {
                  const pingable = d.gateAppliesToRole && d.gateActive
                    && row.version != null && row.version >= d.minAppVersion;
                  const filterVal = row.version == null ? 'unknown' : String(row.version);
                  return (
                    <TableRow key={String(row.version)} hover>
                      <TableCell>{row.version == null ? 'Old app (no version)' : `Build ${row.version}`}</TableCell>
                      <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {d.totalReachable ? `${Math.round((row.count / d.totalReachable) * 1000) / 10}%` : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={pingable ? 'success' : 'default'}
                              label={pingable ? 'Yes' : 'No'} />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="text" endIcon={<OpenInNew fontSize="small" />}
                                onClick={() => drillTo(filterVal)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {d.versions.length === 0 && (
                  <TableRow><TableCell colSpan={5}>
                    <Typography color="textSecondary" sx={{ py: 2 }}>No users with a stored push token match this scope.</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default AppAdoptionPanel;
